import React, { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'

const isoToday = () => new Date().toISOString().slice(0, 10)
const isoPlusDays = (days) => new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)

const initialPayload = () => ({
  vendorBranding: {
    displayName: 'Desert Greens Spray',
    primaryColor: '#3f7a5a',
    logoUrl: 'https://placehold.co/240x80/3f7a5a/ffffff/png?text=Desert+Greens'
  },
  vendorName: 'Desert Greens Spray LLC',
  applicatorLicense: 'AZ-12345',
  pmdNumber: 'PMD-9001',
  qualifyingParty: 'Jane Applicator',
  invoiceNumber: 'SUB-TEST-00001',
  invoiceDate: isoToday(),
  dueDate: isoPlusDays(30),
  paymentTerms: 'Net 30',
  billToName: 'HMI Commercial Landscaping',
  billToAddressLine1: '1234 Desert Way',
  billToAddressLine2: 'Phoenix, AZ 85024',
  lineItems: [
    { description: 'Herbicide application — Tatum Ranch HOA', ratePerAcre: 350, acreage: 3 }
  ],
  adjustments: [],
  siteName: 'Tatum Ranch HOA',
  siteAddress: '5400 E Tatum Blvd, Phoenix AZ',
  sprayDate: isoToday(),
  notes: '',
  footerNote: 'Please remit payment per the payment terms above. Reference the invoice number on all correspondence.'
})

const DESERT_GREENS_PRESET = {
  vendorBranding: { displayName: 'Desert Greens Spray', primaryColor: '#3f7a5a', logoUrl: 'https://placehold.co/240x80/3f7a5a/ffffff/png?text=Desert+Greens' },
  vendorName: 'Desert Greens Spray LLC',
  applicatorLicense: 'AZ-12345',
  pmdNumber: 'PMD-9001',
  qualifyingParty: 'Jane Applicator'
}

const CACTUS_COUNTRY_PRESET = {
  vendorBranding: { displayName: 'Cactus Country Applicators', primaryColor: '#b25a2a', logoUrl: 'https://placehold.co/240x80/b25a2a/ffffff/png?text=Cactus+Country' },
  vendorName: 'Cactus Country Applicators',
  applicatorLicense: 'AZ-67890',
  pmdNumber: 'PMD-8745',
  qualifyingParty: 'Bob Sprayer'
}

const labelStyle = { fontSize: 11, color: '#607d8b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }
const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #cfd9dc', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }
const sectionStyle = { background: '#fff', borderRadius: 6, padding: 14, marginBottom: 12, border: '1px solid #e0e8eb' }

function InvoicePdfPreviewPage({ url }) {
  const [payload, setPayload] = useState(initialPayload())
  const [pdfDataUrl, setPdfDataUrl] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)

  const setField = (key, value) => setPayload(p => ({ ...p, [key]: value }))
  const setBrand = (key, value) => setPayload(p => ({ ...p, vendorBranding: { ...p.vendorBranding, [key]: value } }))

  const setLineField = (idx, key, value) => {
    setPayload(p => ({
      ...p,
      lineItems: p.lineItems.map((li, i) => i === idx ? { ...li, [key]: key === 'description' ? value : Number(value) || 0 } : li)
    }))
  }
  const addLine = () => setPayload(p => ({ ...p, lineItems: [ ...p.lineItems, { description: '', ratePerAcre: 0, acreage: 0 } ] }))
  const removeLine = (idx) => setPayload(p => ({ ...p, lineItems: p.lineItems.filter((_, i) => i !== idx) }))

  const setAdjField = (idx, key, value) => {
    setPayload(p => ({
      ...p,
      adjustments: p.adjustments.map((a, i) => i === idx ? { ...a, [key]: key === 'description' ? value : Number(value) || 0 } : a)
    }))
  }
  const addAdj = () => setPayload(p => ({ ...p, adjustments: [ ...p.adjustments, { description: '', amount: 0 } ] }))
  const removeAdj = (idx) => setPayload(p => ({ ...p, adjustments: p.adjustments.filter((_, i) => i !== idx) }))

  const applyPreset = (preset) => setPayload(p => ({ ...p, ...preset }))

  const generate = useCallback(async () => {
    setGenerating(true)
    setError(null)
    try {
      const base = url || ''
      const res = await axios.post(`${base}/api/invoice-pdf-preview`, payload)
      setPdfDataUrl(res.data?.dataUrl || null)
    } catch (err) {
      console.error('[InvoicePdfPreview] generate error', err)
      setError(err.response?.data?.message || err.message || 'Failed to generate PDF')
    } finally {
      setGenerating(false)
    }
  }, [url, payload])

  const download = () => {
    if (!pdfDataUrl) return
    const a = document.createElement('a')
    a.href = pdfDataUrl
    a.download = `invoice-preview-${payload.invoiceNumber || Date.now()}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', background: '#f5f7f8', overflow: 'hidden' }}>
      {/* LEFT — form */}
      <div style={{ flex: '0 0 480px', overflowY: 'auto', padding: 16, borderRight: '1px solid #e0e8eb' }}>
        <h2 style={{ marginTop: 0, marginBottom: 4, fontSize: 18 }}>Invoice PDF Preview</h2>
        <div style={{ fontSize: 12, color: '#607d8b', marginBottom: 16 }}>
          Edit any field, then click Generate. Hits <code>generateInvoicePDF</code> on the server.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button onClick={() => applyPreset(DESERT_GREENS_PRESET)} style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, background: '#e8f5e9', color: '#2e7d32', border: '1px solid #b6dfc0', borderRadius: 4, cursor: 'pointer' }}>
            Desert Greens
          </button>
          <button onClick={() => applyPreset(CACTUS_COUNTRY_PRESET)} style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, background: '#fce8d6', color: '#8c4a1a', border: '1px solid #f0c89a', borderRadius: 4, cursor: 'pointer' }}>
            Cactus Country
          </button>
          <button onClick={() => setPayload(initialPayload())} style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, background: '#fff3d9', color: '#8c5b00', border: '1px solid #f0c66e', borderRadius: 4, cursor: 'pointer' }}>
            Reset
          </button>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Vendor branding</div>
          <label style={labelStyle}>Display name</label>
          <input style={inputStyle} value={payload.vendorBranding.displayName} onChange={e => setBrand('displayName', e.target.value)} />
          <div style={{ height: 8 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px', gap: 8 }}>
            <div>
              <label style={labelStyle}>Primary color (hex)</label>
              <input style={inputStyle} value={payload.vendorBranding.primaryColor} onChange={e => setBrand('primaryColor', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>&nbsp;</label>
              <input type="color" value={payload.vendorBranding.primaryColor} onChange={e => setBrand('primaryColor', e.target.value)} style={{ width: '100%', height: 30, padding: 0, border: '1px solid #cfd9dc', borderRadius: 4, cursor: 'pointer' }} />
            </div>
          </div>
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Logo URL</label>
          <input style={inputStyle} value={payload.vendorBranding.logoUrl} onChange={e => setBrand('logoUrl', e.target.value)} placeholder="https://..." />
          <div style={{ fontSize: 10, color: '#90a4ae', marginTop: 4 }}>
            Server fetches and base64-encodes. Leave blank to render the display name instead.
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Vendor (Bill From)</div>
          <label style={labelStyle}>Legal name</label>
          <input style={inputStyle} value={payload.vendorName} onChange={e => setField('vendorName', e.target.value)} />
          <div style={{ height: 8 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelStyle}>Applicator License</label>
              <input style={inputStyle} value={payload.applicatorLicense} onChange={e => setField('applicatorLicense', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>PMD #</label>
              <input style={inputStyle} value={payload.pmdNumber} onChange={e => setField('pmdNumber', e.target.value)} />
            </div>
          </div>
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Qualifying Party</label>
          <input style={inputStyle} value={payload.qualifyingParty} onChange={e => setField('qualifyingParty', e.target.value)} />
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Bill To</div>
          <label style={labelStyle}>Name</label>
          <input style={inputStyle} value={payload.billToName} onChange={e => setField('billToName', e.target.value)} />
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Address line 1</label>
          <input style={inputStyle} value={payload.billToAddressLine1} onChange={e => setField('billToAddressLine1', e.target.value)} />
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Address line 2</label>
          <input style={inputStyle} value={payload.billToAddressLine2} onChange={e => setField('billToAddressLine2', e.target.value)} />
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Invoice meta</div>
          <label style={labelStyle}>Invoice #</label>
          <input style={inputStyle} value={payload.invoiceNumber} onChange={e => setField('invoiceNumber', e.target.value)} />
          <div style={{ height: 8 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelStyle}>Invoice date</label>
              <input type="date" style={inputStyle} value={payload.invoiceDate} onChange={e => setField('invoiceDate', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Due date</label>
              <input type="date" style={inputStyle} value={payload.dueDate} onChange={e => setField('dueDate', e.target.value)} />
            </div>
          </div>
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Payment terms</label>
          <input style={inputStyle} value={payload.paymentTerms} onChange={e => setField('paymentTerms', e.target.value)} />
        </div>

        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#263238' }}>Line items</div>
            <button onClick={addLine} style={{ fontSize: 11, padding: '4px 8px', background: '#e3f2fd', color: '#0d47a1', border: '1px solid #b6d4f5', borderRadius: 4, cursor: 'pointer' }}>+ Add</button>
          </div>
          {payload.lineItems.map((li, idx) => (
            <div key={idx} style={{ marginBottom: 10, padding: 10, background: '#f9fafb', border: '1px solid #e6eaec', borderRadius: 4 }}>
              <label style={labelStyle}>Description</label>
              <input style={inputStyle} value={li.description} onChange={e => setLineField(idx, 'description', e.target.value)} />
              <div style={{ height: 6 }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                <div>
                  <label style={labelStyle}>Rate / acre</label>
                  <input type="number" style={inputStyle} value={li.ratePerAcre} onChange={e => setLineField(idx, 'ratePerAcre', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Acreage</label>
                  <input type="number" step="0.1" style={inputStyle} value={li.acreage} onChange={e => setLineField(idx, 'acreage', e.target.value)} />
                </div>
                <button onClick={() => removeLine(idx)} disabled={payload.lineItems.length === 1} style={{ padding: '6px 10px', fontSize: 11, background: '#fff', color: '#c62828', border: '1px solid #f4c7c7', borderRadius: 4, cursor: payload.lineItems.length === 1 ? 'not-allowed' : 'pointer' }}>×</button>
              </div>
            </div>
          ))}
        </div>

        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#263238' }}>Adjustments</div>
            <button onClick={addAdj} style={{ fontSize: 11, padding: '4px 8px', background: '#e3f2fd', color: '#0d47a1', border: '1px solid #b6d4f5', borderRadius: 4, cursor: 'pointer' }}>+ Add</button>
          </div>
          {payload.adjustments.length === 0 && (
            <div style={{ fontSize: 11, color: '#90a4ae', fontStyle: 'italic' }}>No adjustments.</div>
          )}
          {payload.adjustments.map((a, idx) => (
            <div key={idx} style={{ marginBottom: 8, display: 'grid', gridTemplateColumns: '1fr 100px auto', gap: 6, alignItems: 'end' }}>
              <div>
                <label style={labelStyle}>Description</label>
                <input style={inputStyle} value={a.description} onChange={e => setAdjField(idx, 'description', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Amount (±)</label>
                <input type="number" style={inputStyle} value={a.amount} onChange={e => setAdjField(idx, 'amount', e.target.value)} />
              </div>
              <button onClick={() => removeAdj(idx)} style={{ padding: '6px 10px', fontSize: 11, background: '#fff', color: '#c62828', border: '1px solid #f4c7c7', borderRadius: 4, cursor: 'pointer' }}>×</button>
            </div>
          ))}
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#263238' }}>Service details (optional)</div>
          <label style={labelStyle}>Site name</label>
          <input style={inputStyle} value={payload.siteName} onChange={e => setField('siteName', e.target.value)} />
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Site address</label>
          <input style={inputStyle} value={payload.siteAddress} onChange={e => setField('siteAddress', e.target.value)} />
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Service date</label>
          <input type="date" style={inputStyle} value={payload.sprayDate} onChange={e => setField('sprayDate', e.target.value)} />
          <div style={{ height: 8 }} />
          <label style={labelStyle}>Notes</label>
          <textarea style={{ ...inputStyle, minHeight: 60 }} value={payload.notes} onChange={e => setField('notes', e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: 8, position: 'sticky', bottom: 0, padding: '12px 0', background: 'linear-gradient(to bottom, rgba(245,247,248,0), #f5f7f8 30%)' }}>
          <button
            onClick={generate}
            disabled={generating}
            style={{
              flex: 2, padding: '10px 16px', fontSize: 13, fontWeight: 700, color: '#fff',
              background: generating ? '#90a4ae' : '#558b6e',
              border: 'none', borderRadius: 4, cursor: generating ? 'wait' : 'pointer'
            }}
          >
            {generating ? 'Generating…' : 'Generate PDF'}
          </button>
          <button
            onClick={download}
            disabled={!pdfDataUrl}
            style={{
              flex: 1, padding: '10px 16px', fontSize: 13, fontWeight: 700,
              color: pdfDataUrl ? '#263238' : '#90a4ae',
              background: '#fff',
              border: '1px solid ' + (pdfDataUrl ? '#cfd9dc' : '#e0e8eb'),
              borderRadius: 4, cursor: pdfDataUrl ? 'pointer' : 'not-allowed'
            }}
          >
            Download
          </button>
        </div>

        {error && (
          <div style={{ marginTop: 10, padding: 10, background: '#ffe8e8', color: '#c62828', borderRadius: 4, fontSize: 12 }}>
            {error}
          </div>
        )}
      </div>

      {/* RIGHT — PDF preview */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {pdfDataUrl ? (
          <iframe
            title="Invoice PDF preview"
            src={pdfDataUrl}
            style={{ flex: 1, border: 'none', background: '#525659' }}
          />
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#90a4ae', fontSize: 14 }}>
            {generating ? 'Generating PDF…' : 'Click "Generate PDF" to render the invoice.'}
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(InvoicePdfPreviewPage)
